import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import _ from 'lodash';

const libraries: any = ['places'];

const GoogleSearchMap = ({ setLocation, location, placeholder }: any) => {
  const [inputValue, setInputValue] = useState(location?.name || '');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.2090 }); // default: Delhi
  const autocompleteServiceRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDOCJ7V-QaS9nnf7zZ1B7ZEgrkI-YcxWA0', // replace with your key
    libraries,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (!value) return setSuggestions([]);

    if (autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: value },
        (predictions: any[], status: string) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          }
        }
      );
    }
  };

  const handleSelectSuggestion = (place: any) => {
    setInputValue(place.description);
    setSuggestions([]);

    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

    placesService.getDetails({ placeId: place.place_id }, (details: any, status: string) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && details.geometry) {
        const pincodeData: any = _.last(details.geometry.address_components)

        const lat = details.geometry.location.lat();
        const lng = details.geometry.location.lng();
        setLocation({
          lat,
          lng,
          name: details.name,
          description: details.formatted_address,
          pincode: pincodeData?.long_name,
        });
        setMapCenter({ lat, lng });
      }
    });
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !geocoderRef.current) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    geocoderRef.current.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === 'OK' && results[0]) {
        const pincodeData: any = _.last(results[0].address_components)
        
        setLocation({
          lat,
          lng,
          name: results[0].address_components?.[0]?.long_name || '',
          description: results[0].formatted_address,
          pincode: pincodeData?.long_name,
        });
        setInputValue(results[0].formatted_address);
      }
    });

    setMapCenter({ lat, lng });
  };

  const clearSearch = () => {
    setInputValue('');
    setSuggestions([]);
    setLocation(null);
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="w-full">
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder || 'Search for location'}
          className="w-full text-black border px-4 py-2 rounded-md"
        />
        {inputValue && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-2 text-gray-500 text-sm"
          >
            Clear
          </button>
        )}
        {suggestions.length > 0 && (
          <ul className="absolute z-10 bg-white shadow-lg w-full mt-1 rounded max-h-60 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.place_id}
                className="px-4 py-2 text-black hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion.description}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="w-full h-[300px] mt-4">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={location ? 15 : 5}
          onClick={handleMapClick}
        >
          {location && <Marker position={{ lat: location.lat, lng: location.lng }} />}
        </GoogleMap>
      </div>
    </div>
  );
};

export default GoogleSearchMap;
